// Typy odpovidaji DTO z monitoring-be. Kdyz se meni tam, meni se i tady —
// backend zadny klientsky kod negeneruje, schvalne: rucne udrzovany soubor
// je kratsi nez generator a je videt, co UI opravdu pouziva.

export type Role = 'ADMIN' | 'MEMBER'
export type TargetMethod = 'GET' | 'POST' | 'HEAD'
export type TargetStatus = 'UP' | 'DOWN' | 'UNKNOWN'
export type OverallStatus = 'OPERATIONAL' | 'DEGRADED' | 'OUTAGE' | 'UNKNOWN'

/** Okna, ktera backend zna (StatsWindow). */
export const STATS_WINDOWS = ['1h', '24h', '7d', '30d'] as const
export type StatsWindow = (typeof STATS_WINDOWS)[number]

/** Jeden den v dennim pruhu. `uptimePercent: null` = den bez mereni. */
export interface DailyUptime {
  day: string
  uptimePercent: number | null
  samples: number
}

export interface User {
  id: string
  email: string
  role: Role
  customerId: string
  createdAt: string
}

export interface LoginResponse {
  token: string
  expiresAt: string
  user: User
}

export interface Service {
  id: string
  name: string
  description: string | null
  isPublic: boolean
  slug: string
  /** Adresa verejne status stranky: /status/{publicId}. */
  publicId: string
  /** Token sdileneho odkazu /share/{token}; null = sdileni je vypnute. */
  shareToken: string | null
  targetCount: number
  createdAt: string
  updatedAt: string
}

export interface Target {
  id: string
  serviceId: string
  name: string
  url: string
  method: TargetMethod
  intervalSeconds: number
  expectedStatusCode: number
  timeoutMs: number
  isPublic: boolean
  isActive: boolean
  currentStatus: TargetStatus
  lastCheckedAt: string | null
  nextCheckAt: string | null
  lastResponseTimeMs: number | null
  lastStatusCode: number | null
  lastErrorMessage: string | null
  uptime24h: number | null
  createdAt: string
}

/** Denni pruh jednoho cile pro prehled nad tabulkou sluzby. */
export interface TargetDaily {
  targetId: string
  name: string
  status: TargetStatus
  uptimePercent: number | null
  daily: DailyUptime[]
}

export interface ServiceDetail {
  service: Service
  targets: Target[]
}

export interface ServiceRequest {
  name: string
  description?: string | null
  isPublic: boolean
  slug?: string | null
}

export interface TargetRequest {
  name: string
  url: string
  method: TargetMethod
  intervalSeconds: number
  expectedStatusCode: number
  timeoutMs: number
  isPublic: boolean
  isActive: boolean
}

export interface UptimeStats {
  window: string
  from: string
  to: string
  samples: number
  upSamples: number
  uptimePercent: number | null
  avgResponseMs: number | null
  p95ResponseMs: number | null
  minResponseMs: number | null
  maxResponseMs: number | null
}

export interface TimelinePoint {
  bucketStart: string
  samples: number
  /** null = v tomhle kosi se nemerilo. Nula by znamenala vypadek. */
  uptimePercent: number | null
  avgResponseMs: number | null
  maxResponseMs: number | null
}

export interface PingResult {
  id: number | null
  checkedAt: string
  statusCode: number | null
  responseTimeMs: number
  isUp: boolean
  errorMessage: string | null
}

export interface ServiceStatus {
  id: string
  name: string
  slug: string
  isPublic: boolean
  /** Adresa verejne status stranky: /status/{publicId}. */
  publicId: string
  overallStatus: OverallStatus
  targetCount: number
  upCount: number
  downCount: number
  unknownCount: number
  uptime24h: number | null
  downTargets: Target[]
}

export interface Dashboard {
  serviceCount: number
  targetCount: number
  upCount: number
  downCount: number
  unknownCount: number
  uptime24h: number | null
  services: ServiceStatus[]
  generatedAt: string
}

/** Cil na sdilene (read-only) strance sluzby. */
export interface ShareTarget {
  id: string
  name: string
  url: string
  method: TargetMethod
  intervalSeconds: number
  status: TargetStatus
  isActive: boolean
  lastCheckedAt: string | null
  lastResponseTimeMs: number | null
  lastStatusCode: number | null
  uptime24h: number | null
  uptimeWindow: number | null
  daily: DailyUptime[]
}

export interface ShareService {
  name: string
  description: string | null
  overallStatus: OverallStatus
  windowDays: number
  targets: ShareTarget[]
  generatedAt: string
}

// --- Verejne API (bez prihlaseni) -------------------------------------------

export interface PublicTarget {
  id: string
  name: string
  status: TargetStatus
  lastCheckedAt: string | null
  uptime24h: number | null
  avgResponseMs: number | null
  uptime90d: number | null
  daily: DailyUptime[]
}

export interface PublicService {
  name: string
  description: string | null
  publicId: string
  overallStatus: OverallStatus
  windowDays: number
  targets: PublicTarget[]
  generatedAt: string
}

export interface PublicTargetDetail {
  target: PublicTarget
  serviceName: string
  servicePublicId: string | null
}
